import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { ToastContainer } from './ToastContainer';

export type ToastType = 'success' | 'error' | 'info';

export interface ToastMessage {
  type?: ToastType;
  message: string;
}

export interface ToastItem {
  id: number;
  type: ToastType;
  message: string;
}

interface ToastContextValue {
  notify: (m: ToastMessage) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const MAX_VISIBLE_TOASTS = 3;
const TOAST_DURATION_MS = 4000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const idRef = useRef(0);
  const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const notify = useCallback(({ type = 'info', message }: ToastMessage) => {
    setToasts((prev) => [...prev, { id: ++idRef.current, type, message }]);
  }, []);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // Reconciliation: only the first MAX_VISIBLE_TOASTS are visible, and only
  // those get a 4s auto-dismiss timer. A queued toast's timer starts the
  // moment it becomes visible (slice re-computes), giving it the full 4s
  // on-screen. Manual close clears that toast's timer only.
  useEffect(() => {
    const visible = new Set(toasts.slice(0, MAX_VISIBLE_TOASTS).map((t) => t.id));

    for (const [id, timer] of timersRef.current) {
      if (!visible.has(id)) {
        clearTimeout(timer);
        timersRef.current.delete(id);
      }
    }

    for (const toast of toasts.slice(0, MAX_VISIBLE_TOASTS)) {
      if (!timersRef.current.has(toast.id)) {
        const timer = setTimeout(() => dismiss(toast.id), TOAST_DURATION_MS);
        timersRef.current.set(toast.id, timer);
      }
    }
  }, [toasts, dismiss]);

  // Clear all timers on provider unmount
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const timer of timers.values()) clearTimeout(timer);
      timers.clear();
    };
  }, []);

  return (
    <ToastContext.Provider value={{ notify }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
}